import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { DataStore } from '../models/data-store.model';
import { environment } from '../../../../../../environments/environment';
import { GetDataStoreRequest } from '../models/get-data-store-request';
import { GetDataStoreListResponse } from '../models/get-data-store-list.response';
import { convertKeysToCamelCase } from '../../../../../shared/utilities/json-key-transform';
import { GetDataStoreResponse } from '../models/get-data-store.response';
import { CreateDataStoreRequest } from '../models/create-data-store.request';
import { CreateDataStoreResponse } from '../models/create-data-store.response';
import { DataStoreSignalRNotification } from '../../../../../core/models/signal-r-notification.model';
import { DataStoreProvisionStatusType } from '../models/data-store-provision-status';
import { ToastService } from '../../../../../core/services/toast.service';

@Injectable({
  providedIn: 'root'
})
export class DataStoreService {
  private http = inject(HttpClient);
  private apiUrl = environment.dataStoreApi.serviceBaseUrl;

  private lastRequestedTenantId = signal<string | null>(null);

  toastService = inject(ToastService);

  dataLakes = signal<DataStore[]>([]);
  landingZones = signal<DataStore[]>([]);

  isDataLakeLoading = signal(false);
  isLandingZoneLoading = signal(false);

  dataLakesErrorMessage = signal<string>("");
  landingZonesErrorMessage = signal<string>("");

  getDataLakeList(request: GetDataStoreRequest): void {
    this.isDataLakeLoading.set(true);
    this.dataLakesErrorMessage.set("");
    this.dataLakes.set([]);
    this.lastRequestedTenantId.set(request.tenantId);

    this.http.post<GetDataStoreListResponse>(this.apiUrl+"/GetDataLakeList", request).pipe(
      map(response => {
        this.isDataLakeLoading.set(false);
        const camelCase = convertKeysToCamelCase(response) as GetDataStoreListResponse;
        // convert date time to a standard format for display and conversion into local time
        return camelCase.dataStores.map(dataStore => ({
          ...dataStore,
          createDateTimeUtc: this.parseUtc(dataStore.createDateTimeUtc as any)
        }));
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error.status + ": " + error.statusText);
      })
    ).subscribe({
      next: (dataLakes) => {
        if (request.tenantId === this.lastRequestedTenantId()) {
          this.dataLakes.set(dataLakes);
          this.isDataLakeLoading.set(false);
        }
      },
      error: (error: string) => {
        if (request.tenantId === this.lastRequestedTenantId()) {
          this.dataLakes.set([]);
          this.dataLakesErrorMessage.set("Failed to get data lakes. Please try again later. ("+error+")");
          this.isDataLakeLoading.set(false);
        }
      }
    });
  }

  getLandingZoneList(request: GetDataStoreRequest): void {
    this.isLandingZoneLoading.set(true);
    this.landingZonesErrorMessage.set("");
    this.dataLakes.set([]);
    this.lastRequestedTenantId.set(request.tenantId);

    this.http.post<GetDataStoreListResponse>(this.apiUrl+"/GetLandingZoneList", request).pipe(
      map(response => {
        this.isLandingZoneLoading.set(false);
        const camelCase = convertKeysToCamelCase(response) as GetDataStoreListResponse;
        // convert date time to a standard format for display and conversion into local time
        return camelCase.dataStores.map(dataStore => ({
          ...dataStore,
          createDateTimeUtc: this.parseUtc(dataStore.createDateTimeUtc as any)
        }));
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error.status + ": " + error.statusText);
      })
    ).subscribe({
      next: (landingZones) => {
        if (request.tenantId === this.lastRequestedTenantId()) {
          this.landingZones.set(landingZones);
          this.isLandingZoneLoading.set(false);
        }
      },
      error: (error : string) => {
        if (request.tenantId === this.lastRequestedTenantId()) {
          this.landingZones.set([]);
          this.landingZonesErrorMessage.set("Failed to get landing zones. Please try again later. ("+error+")");
          this.isLandingZoneLoading.set(false);
        }
      }
    });
  }

  getDataLake(request: GetDataStoreRequest): Observable<DataStore> {
    this.isDataLakeLoading.set(true);
    return this.http.post<GetDataStoreResponse>(this.apiUrl+"/GetDataLake", request).pipe(
      map(response => {
        this.isDataLakeLoading.set(false);
        const camelCase = convertKeysToCamelCase(response) as GetDataStoreResponse;
        camelCase.dataStore.createDateTimeUtc = this.parseUtc(camelCase.dataStore.createDateTimeUtc as any);
        return camelCase.dataStore;
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error);
      })
    );
  }

  getLandingZone(request: GetDataStoreRequest): Observable<DataStore> {
    this.isLandingZoneLoading.set(true);
    return this.http.post<GetDataStoreResponse>(this.apiUrl+"/GetLandingZone", request).pipe(
      map(response => {
        this.isLandingZoneLoading.set(false);
        const camelCase = convertKeysToCamelCase(response) as GetDataStoreResponse;
        camelCase.dataStore.createDateTimeUtc = this.parseUtc(camelCase.dataStore.createDateTimeUtc as any);
        return camelCase.dataStore;
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error);
      })
    );
  }

  createDataLake(request: CreateDataStoreRequest): Observable<string> {
    return this.http.post<CreateDataStoreResponse>(this.apiUrl+"/CreateDataLake", request).pipe(
      map(response => {
        const camelCase = convertKeysToCamelCase(response) as CreateDataStoreResponse;
        if (camelCase.error !== null) {
          throw new Error(camelCase.error || 'An unexpected error occurred');
        }
        return camelCase.dataStoreId;
      }),
      catchError((error: HttpErrorResponse)=> {
        error = convertKeysToCamelCase(error);
        return throwError(() => error);
      })
    );
  }

  createLandingZone(request: CreateDataStoreRequest): Observable<string> {
    return this.http.post<CreateDataStoreResponse>(this.apiUrl+"/CreateLandingZone", request).pipe(
      map(response => {
        const camelCase = convertKeysToCamelCase(response) as CreateDataStoreResponse;
        if (camelCase.error !== null) {
          throw new Error(camelCase.error || 'An unexpected error occurred');
        }
        return camelCase.dataStoreId;
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error);
      })
    );
  }

  handleDataStoreUpdate(notification: DataStoreSignalRNotification): void {
    const currentDatalakes = this.dataLakes();
    const currentLandingZones = this.landingZones();

    if (!currentDatalakes || !currentLandingZones) return;

    const dataLakeIndex = currentDatalakes.findIndex(dataStore => dataStore.dataStoreId === notification.UIStateParams.dataStoreId);
    const landingZoneIndex = currentLandingZones.findIndex(dataStore => dataStore.dataStoreId === notification.UIStateParams.dataStoreId);

    let updated = false;
    if (dataLakeIndex !== -1) {      
      currentDatalakes[dataLakeIndex].provisionStatusTypeId = notification.data.provisionStatus;
      if (notification.data.createDateTimeUtc) {
        currentDatalakes[dataLakeIndex].createDateTimeUtc = notification.data.createDateTimeUtc;
      }
      updated = true;  
    } else if (landingZoneIndex !== -1) {
      currentLandingZones[landingZoneIndex].provisionStatusTypeId = notification.data.provisionStatus;
      if (notification.data.createDateTimeUtc) {
        currentLandingZones[landingZoneIndex].createDateTimeUtc = notification.data.createDateTimeUtc;
      }
      updated = true; 
    }

    if (updated) {
      this.dataLakes.set(currentDatalakes);
      this.landingZones.set(currentLandingZones);
    }

    if (notification.data.provisionStatus === DataStoreProvisionStatusType.Created) {
        this.toastService.showSuccess(notification.message);
    } else if (notification.data.provisionStatus === DataStoreProvisionStatusType.Errored) {
        this.toastService.showError(notification.message);
    }
  }

  private parseUtc(dateStr: string): string {
    if (!dateStr) return '';

    // append Z to string, tells the date pipeline that it is UTC as without it, doesn't know what timezone the date is for
    const utcDate = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');

    return utcDate.toISOString(); 
  }
}
